import { Box } from '@mui/material';
import { ReactNode } from 'react';

import { containerQuery } from '@/styles/containerQueries';

import {
  COMPACT_GRAPH_SPACE,
  RENDER_CONFIG,
  WIDE_GRAPH_SPACE,
} from './householdLoadingGraph.config';

/** Owns graph sizing and CSS reveal animations, including reduced motion. */
export function HouseholdLoadingGraphFrame({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        maxWidth: WIDE_GRAPH_SPACE.width,
        aspectRatio: WIDE_GRAPH_SPACE.width / WIDE_GRAPH_SPACE.height,
        mx: 'auto',
        // Reserve space from the container width, not asynchronous worker results.
        [containerQuery(theme).down(RENDER_CONFIG.COMPACT_CONTAINER_MAX_WIDTH)]:
          {
            maxWidth: RENDER_CONFIG.COMPACT_GRAPH_MAX_WIDTH,
            aspectRatio: COMPACT_GRAPH_SPACE.width / COMPACT_GRAPH_SPACE.height,
          },
        '@keyframes graphPulse': {
          '0%, 100%': { opacity: 0.6, transform: 'scale(0.94)' },
          '50%': { opacity: 1, transform: 'scale(1.1)' },
        },
        '@keyframes graphAppear': {
          from: { opacity: 0, transform: 'scale(0.6)' },
          to: { opacity: 1, transform: 'scale(1)' },
        },
        '@keyframes graphFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        '@keyframes graphDraw': {
          from: { strokeDashoffset: 1 },
          to: { strokeDashoffset: 0 },
        },
        '& .graph-reveal': {
          animation: 'graphAppear 900ms cubic-bezier(0.22, 1, 0.36, 1) both',
        },
        '& .graph-label-reveal': {
          animation: 'graphFadeIn 1200ms ease-out 600ms both',
        },
        '& .graph-edge': {
          strokeDasharray: 1,
          animation:
            'graphDraw 1400ms cubic-bezier(0.22, 1, 0.36, 1) 200ms both',
        },
        '& .graph-cross-edge': {
          strokeDasharray: '2 4',
          animation: 'graphFadeIn 1400ms ease-out 200ms both',
        },
        '& .graph-halo': { animation: 'graphPulse 6s ease-in-out infinite' },
        '@media (prefers-reduced-motion: reduce)': {
          '& *': {
            animation: 'none !important',
            transition: 'none !important',
          },
          '& .graph-particle': { display: 'none' },
        },
      })}
    >
      {children}
    </Box>
  );
}
